import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {MapOverlayInterface} from '../interfaces/areaal';
import {HttpService2} from '../services/http-service-2.0/http2.0.service';
import {LoggerService} from '../services/logger/logger.service';
import Utils from './utils';

type AxisOrder = 'xy' | 'yx';
type ExpectedSrs = 'CRS:84' | 'EPSG:4326' | 'EPSG:3857' | 'EPSG:28992' | 'AUTO';

interface RequestVariant {
    url: string;
    expectedSrs: ExpectedSrs;
}

interface WfsViewportBounds {
    south: number;
    west: number;
    north: number;
    east: number;
}

interface WfsFeatureCountResult {
    count: number;
    geometryType: string | null;
    samplePosition: {lat: number, lng: number} | null;
}

interface WfsLayerCountResult {
    layerName: string;
    count: number;
    geometryType: string | null;
    samplePosition: {lat: number, lng: number} | null;
}

interface WfsFeatureCountByGeometryResult {
    totalCount: number;
    countsByGeometry: {[geometryType: string]: number};
    layerCounts: WfsLayerCountResult[];
}

// CRS strategy:
// 1) Request WFS in a deterministic order, optionally prioritized by server settings.
// 2) Normalize to WGS84 lon/lat only when the payload proves conversion is needed.
// 3) Keep already-correct geographic payloads untouched to avoid cross-layer drift.
//
// Quick decision table:
// - RD: projected meters in Dutch bounds, convert with Utils.rd2wgs_2().
// - Mercator: projected meters far outside lon/lat bounds, convert from spherical mercator.
// - Swapped axis: geographic-looking values, but only lat/lon or lon/lat one way lands in NL.
// - Already correct: geographic coordinates already match the expected order, leave as-is.
export default class MapWFSLayerGenerator {
    private static readonly MAX_WFS_FEATURES_REQUEST: number | null = null;
    private static readonly MID_WFS_FEATURES_REQUEST = 1000;
    private static readonly LOW_WFS_FEATURES_REQUEST = 500;
    private static readonly GEOJSON_CACHE_TTL_MS = 120000;
    private static readonly GEOJSON_CACHE_MAX_ENTRIES = 500;
    private static readonly FEATURE_COUNT_CACHE_TTL_MS = 90000;
    private static readonly FEATURE_COUNT_CACHE_MAX_ENTRIES = 120;

    private geoJsonCache: {[cacheKey: string]: {expiresAt: number, data: any}} = {};
    private featureCountCache: {[cacheKey: string]: {expiresAt: number, data: WfsFeatureCountByGeometryResult}} = {};

    constructor(private httpService: HttpService2, protected logger: LoggerService) {
    }

    public fetchGeoJson(server: MapOverlayInterface, viewportBounds: WfsViewportBounds | null = null, zoomLevel: number | null = null): Observable<any | null> {
        const cacheKey = this.getGeoJsonCacheKey(server, viewportBounds, zoomLevel);
        const cachedGeoJson = this.getCachedGeoJson(cacheKey);
        if (cachedGeoJson) {
            return of(cachedGeoJson);
        }

        const selectedLayers = (server.layers || [])
            .map(_layer => _layer.value)
            .filter(_value => !!_value);

        if (selectedLayers.length <= 0) {
            return of(null);
        }

        // Fetch each feature type separately and merge the results. GeoServer treats
        // comma-separated typeNames in a single GetFeature as a JOIN query and fails
        // with "Join query must specify a filter", so each layer needs its own request.
        const layerRequests = selectedLayers.map((layer) => {
            const requestVariants = this.getWFSGeoJsonRequestVariants(server, viewportBounds, zoomLevel, [layer]);
            if (requestVariants.length <= 0) {
                return of(null);
            }

            return this.tryFetchByIndex(server, requestVariants, 0);
        });

        return forkJoin(layerRequests).pipe(
            map((layerResults: (any | null)[]) => {
                const mergedGeoJson = this.mergeFeatureCollections(layerResults);
                if (!mergedGeoJson || !Array.isArray(mergedGeoJson.features) || mergedGeoJson.features.length === 0) {
                    return null;
                }

                this.storeGeoJsonInCache(cacheKey, mergedGeoJson);

                return mergedGeoJson;
            }),
        );
    }

    public fetchFeatureCount(server: MapOverlayInterface, viewportBounds: WfsViewportBounds | null = null): Observable<WfsFeatureCountByGeometryResult> {
        const selectedLayers = (server.layers || [])
            .map(_layer => _layer.value)
            .filter(_value => !!_value);

        if (selectedLayers.length <= 0) {
            return of({
                totalCount: 0,
                countsByGeometry: {},
                layerCounts: [],
            });
        }

        const cacheKey = this.getFeatureCountCacheKey(server, viewportBounds, selectedLayers);
        const cachedResult = this.getCachedFeatureCount(cacheKey);
        if (cachedResult) {
            return of(cachedResult);
        }

        const layerRequests = selectedLayers.map((layerName) => {
            const requestVariants = this.getWFSCountRequestVariants(server, viewportBounds, [layerName]);
            if (requestVariants.length <= 0) {
                return of({layerName: layerName, count: 0, geometryType: null, samplePosition: null});
            }

            return this.tryFetchCountByIndex(requestVariants, 0).pipe(
                map((result: WfsFeatureCountResult) => {
                    return {
                        layerName: layerName,
                        count: result?.count || 0,
                        geometryType: result?.geometryType || null,
                        samplePosition: result?.samplePosition || null,
                    };
                }),
            );
        });

        return forkJoin(layerRequests).pipe(
            map((results: WfsLayerCountResult[]) => {
                const countsByGeometry: {[geometryType: string]: number} = {};
                let totalCount = 0;

                results.forEach((result) => {
                    if (!result || !result.count || result.count <= 0) {
                        return;
                    }

                    const geometryType = result.geometryType || 'Unknown';
                    countsByGeometry[geometryType] = (countsByGeometry[geometryType] || 0) + result.count;
                    totalCount += result.count;
                });

                const result = {
                    totalCount: totalCount,
                    countsByGeometry: countsByGeometry,
                    layerCounts: results,
                };

                this.storeFeatureCountInCache(cacheKey, result);

                return result;
            }),
        );
    }

    private getFeatureCountCacheKey(server: MapOverlayInterface, viewportBounds: WfsViewportBounds | null, selectedLayers: string[]): string | null {
        if (!server || selectedLayers.length <= 0) {
            return null;
        }

        const sortedLayers = selectedLayers
            .filter((_layer) => !!_layer)
            .sort()
            .join(',');

        if (!sortedLayers) {
            return null;
        }

        const boundsToken = viewportBounds
            ? [
                Number(viewportBounds.south.toFixed(4)),
                Number(viewportBounds.west.toFixed(4)),
                Number(viewportBounds.north.toFixed(4)),
                Number(viewportBounds.east.toFixed(4)),
            ].join(':')
            : 'all';

        return `${server.id}|${server.url}|${sortedLayers}|${boundsToken}`;
    }

    private getCachedFeatureCount(cacheKey: string | null): WfsFeatureCountByGeometryResult | null {
        if (!cacheKey) {
            return null;
        }

        const cachedEntry = this.featureCountCache[cacheKey];
        if (!cachedEntry) {
            return null;
        }

        if (cachedEntry.expiresAt <= Date.now()) {
            delete this.featureCountCache[cacheKey];
            return null;
        }

        return cachedEntry.data;
    }

    private storeFeatureCountInCache(cacheKey: string | null, data: WfsFeatureCountByGeometryResult): void {
        if (!cacheKey || !data) {
            return;
        }

        this.featureCountCache[cacheKey] = {
            expiresAt: Date.now() + MapWFSLayerGenerator.FEATURE_COUNT_CACHE_TTL_MS,
            data: data,
        };

        this.evictFeatureCountCacheEntries();
    }

    private evictFeatureCountCacheEntries(): void {
        const cacheKeys = Object.keys(this.featureCountCache);
        if (cacheKeys.length <= MapWFSLayerGenerator.FEATURE_COUNT_CACHE_MAX_ENTRIES) {
            return;
        }

        cacheKeys.sort((a, b) => this.featureCountCache[a].expiresAt - this.featureCountCache[b].expiresAt);
        const entriesToRemove = cacheKeys.length - MapWFSLayerGenerator.FEATURE_COUNT_CACHE_MAX_ENTRIES;

        for (let i = 0; i < entriesToRemove; i++) {
            delete this.featureCountCache[cacheKeys[i]];
        }
    }

    private tryFetchByIndex(server: MapOverlayInterface, requestVariants: RequestVariant[], index: number): Observable<any | null> {
        if (index >= requestVariants.length) {
            this.logger.log('[MapWFSLayerGenerator] WFS load failed for server after all CRS attempts: ', server);
            return of(null);
        }

        const requestVariant = requestVariants[index];
        const requestUrl = requestVariant.url;

        return this.httpService.doExternalGetRequest(requestUrl, 'text').pipe(
            switchMap((rawText: any) => {
                let parsedGeoJson = this.parseWFSGeoJson(rawText);

                if (!parsedGeoJson || !parsedGeoJson.features || parsedGeoJson.features.length === 0) {
                    return this.tryFetchByIndex(server, requestVariants, index + 1);
                }

                parsedGeoJson = this.normalizeWFSGeoJson(parsedGeoJson, server, requestVariant.expectedSrs);

                if (!parsedGeoJson || !parsedGeoJson.features || parsedGeoJson.features.length === 0) {
                    return this.tryFetchByIndex(server, requestVariants, index + 1);
                }

                return of(parsedGeoJson);
            }),
            catchError((error: any) => {
                this.logger.log('[MapWFSLayerGenerator] WFS load failed for URL, trying fallback: ' + requestUrl);
                this.logger.log('[MapWFSLayerGenerator] WFS load error details: ', error);
                return this.tryFetchByIndex(server, requestVariants, index + 1);
            })
        );
    }

    private getGeoJsonCacheKey(server: MapOverlayInterface, viewportBounds: WfsViewportBounds | null, zoomLevel: number | null): string | null {
        if (!server || !viewportBounds) {
            return null;
        }

        const selectedLayers = (server.layers || [])
            .map(_layer => _layer.value)
            .filter(_value => !!_value)
            .sort()
            .join(',');

        if (!selectedLayers) {
            return null;
        }

        const round = (value: number) => Number(value.toFixed(4));
        const zoomToken = zoomLevel === null || zoomLevel === undefined ? 'na' : String(Math.floor(zoomLevel));
        const boundsToken = [
            round(viewportBounds.south),
            round(viewportBounds.west),
            round(viewportBounds.north),
            round(viewportBounds.east),
        ].join(':');

        return `${server.id}|${server.url}|${zoomToken}|${selectedLayers}|${boundsToken}`;
    }

    private getCachedGeoJson(cacheKey: string | null): any | null {
        if (!cacheKey) {
            return null;
        }

        const cachedEntry = this.geoJsonCache[cacheKey];
        if (!cachedEntry) {
            return null;
        }

        if (cachedEntry.expiresAt <= Date.now()) {
            delete this.geoJsonCache[cacheKey];
            return null;
        }

        return cachedEntry.data;
    }

    private storeGeoJsonInCache(cacheKey: string | null, data: any): void {
        if (!cacheKey || !data) {
            return;
        }

        this.geoJsonCache[cacheKey] = {
            expiresAt: Date.now() + MapWFSLayerGenerator.GEOJSON_CACHE_TTL_MS,
            data: data,
        };

        this.evictGeoJsonCacheEntries();
    }

    private evictGeoJsonCacheEntries(): void {
        const cacheKeys = Object.keys(this.geoJsonCache);
        if (cacheKeys.length <= MapWFSLayerGenerator.GEOJSON_CACHE_MAX_ENTRIES) {
            return;
        }

        cacheKeys.sort((a, b) => this.geoJsonCache[a].expiresAt - this.geoJsonCache[b].expiresAt);
        const entriesToRemove = cacheKeys.length - MapWFSLayerGenerator.GEOJSON_CACHE_MAX_ENTRIES;

        for (let i = 0; i < entriesToRemove; i++) {
            delete this.geoJsonCache[cacheKeys[i]];
        }
    }

    private mergeFeatureCollections(collections: (any | null)[]): any | null {
        const validCollections = (collections || []).filter((collection) => collection && Array.isArray(collection.features));
        if (validCollections.length <= 0) {
            return null;
        }

        const mergedFeatures: any[] = [];
        validCollections.forEach((collection) => {
            mergedFeatures.push(...collection.features);
        });

        return {
            ...validCollections[0],
            features: mergedFeatures,
        };
    }

    private tryFetchCountByIndex(requestVariants: RequestVariant[], index: number, bestResult: WfsFeatureCountResult | null = null): Observable<WfsFeatureCountResult> {
        if (index >= requestVariants.length) {
            return of(bestResult || {count: 0, geometryType: null, samplePosition: null});
        }

        const requestVariant = requestVariants[index];
        const requestUrl = requestVariant.url;

        return this.httpService.doExternalGetRequest(requestUrl, 'text').pipe(
            switchMap((rawText: any) => {
                const countResult = this.parseWFSFeatureCount(rawText, requestVariant.expectedSrs);
                if (!countResult) {
                    return this.tryFetchCountByIndex(requestVariants, index + 1, bestResult);
                }

                const previousBest = bestResult || {count: 0, geometryType: null, samplePosition: null};
                const highestCount = Math.max(previousBest.count || 0, countResult.count || 0);
                const preferredGeometryType = countResult.geometryType || previousBest.geometryType || null;
                const preferredSamplePosition = countResult.samplePosition || previousBest.samplePosition || null;
                const nextBestResult: WfsFeatureCountResult = {
                    count: highestCount,
                    geometryType: preferredGeometryType,
                    samplePosition: preferredSamplePosition,
                };

                // Keep fallback behavior aligned with feature requests.
                // Some WFS servers return a valid 0 for unsupported CRS/bbox combinations.
                if (countResult.count === 0 && index < requestVariants.length - 1) {
                    return this.tryFetchCountByIndex(requestVariants, index + 1, nextBestResult);
                }

                // Continue searching when we only have a count but still miss geometry type metadata.
                if (!countResult.geometryType && index < requestVariants.length - 1) {
                    return this.tryFetchCountByIndex(requestVariants, index + 1, nextBestResult);
                }

                return of(nextBestResult);
            }),
            catchError((_error: any) => {
                return this.tryFetchCountByIndex(requestVariants, index + 1, bestResult);
            })
        );
    }

    private getWFSGeoJsonRequestVariants(server: MapOverlayInterface, viewportBounds: WfsViewportBounds | null, zoomLevel: number | null, selectedLayersOverride: string[] | null = null): RequestVariant[] {
        const selectedLayers = selectedLayersOverride && selectedLayersOverride.length > 0
            ? selectedLayersOverride
            : (server.layers || [])
                .map(_layer => _layer.value)
                .filter(_value => !!_value);

        if (selectedLayers.length <= 0) {
            return [];
        }

        const maxFeatures = this.getMaxFeaturesForZoom(zoomLevel);
        const param = server.url.includes('?') ? '&' : '?';
        const countParam = maxFeatures === null ? '' : `&count=${maxFeatures}`;
        const outputFormats = this.getPreferredOutputFormats(server);
        const expectedSrsOrder = this.getPreferredExpectedSrsOrder(server);

        const buildVariantsForBaseUrl = (baseUrl: string): RequestVariant[] => {
            const defaultVariants: RequestVariant[] = this.buildDefaultCrsVariants(baseUrl, expectedSrsOrder);

            if (!viewportBounds) {
                return defaultVariants;
            }

            const rdBboxParam = this.getRdBboxParameter(viewportBounds);
            const bboxParam = this.getCrs84BboxParameter(viewportBounds);
            const boundedVariants: RequestVariant[] = this.buildBoundedCrsVariants(baseUrl, expectedSrsOrder, rdBboxParam, bboxParam);

            return [...boundedVariants, ...defaultVariants];
        };

        // Callers request a single feature type at a time; comma-joined typeNames are a
        // GeoServer JOIN query and are rejected without a filter.
        const typeNames = selectedLayers.join(',');
        const variants: RequestVariant[] = [];

        for (const format of outputFormats) {
            const baseUrl = `${server.url}${param}service=WFS&request=GetFeature&version=2.0.0&typeNames=${encodeURIComponent(typeNames)}&outputFormat=${encodeURIComponent(format)}${countParam}`;
            variants.push(...buildVariantsForBaseUrl(baseUrl));
        }

        return variants;
    }

    private getWFSCountRequestVariants(server: MapOverlayInterface, viewportBounds: WfsViewportBounds | null, selectedLayersOverride: string[] | null = null): RequestVariant[] {
        const selectedLayers = selectedLayersOverride && selectedLayersOverride.length > 0
            ? selectedLayersOverride
            : (server.layers || [])
                .map(_layer => _layer.value)
                .filter(_value => !!_value);

        if (selectedLayers.length <= 0) {
            return [];
        }

        const param = server.url.includes('?') ? '&' : '?';
        const outputFormats = this.getPreferredOutputFormats(server);
        const expectedSrsOrder = this.getPreferredExpectedSrsOrder(server);
        const baseUrlHits = (layer: string, format: string) => `${server.url}${param}service=WFS&request=GetFeature&version=2.0.0&typeNames=${encodeURIComponent(layer)}&resultType=hits&outputFormat=${encodeURIComponent(format)}`;
        // Hits without outputFormat: ArcGIS and other servers return XML numberMatched even without a format hint.
        const baseUrlHitsXml = (layer: string) => `${server.url}${param}service=WFS&request=GetFeature&version=2.0.0&typeNames=${encodeURIComponent(layer)}&resultType=hits`;
        const baseUrlFallback = (layer: string, format: string) => `${server.url}${param}service=WFS&request=GetFeature&version=2.0.0&typeNames=${encodeURIComponent(layer)}&outputFormat=${encodeURIComponent(format)}&count=1`;

        const buildVariantsForBaseUrl = (baseUrl: string): RequestVariant[] => {
            const defaultVariants: RequestVariant[] = this.buildDefaultCrsVariants(baseUrl, expectedSrsOrder);

            if (!viewportBounds) {
                return defaultVariants;
            }

            const rdBboxParam = this.getRdBboxParameter(viewportBounds);
            const bboxParam = this.getCrs84BboxParameter(viewportBounds);
            const boundedVariants: RequestVariant[] = this.buildBoundedCrsVariants(baseUrl, expectedSrsOrder, rdBboxParam, bboxParam);

            return [...boundedVariants, ...defaultVariants];
        };

        // GeoServer join queries with spatial filters fail. If multiple layers + bbox, request each layer separately.
        if (selectedLayers.length > 1 && viewportBounds) {
            const variants: RequestVariant[] = [];
            for (const format of outputFormats) {
                for (const layer of selectedLayers) {
                    variants.push(
                        ...buildVariantsForBaseUrl(baseUrlHits(layer, format)),
                        ...buildVariantsForBaseUrl(baseUrlHitsXml(layer)),
                        ...buildVariantsForBaseUrl(baseUrlFallback(layer, format))
                    );
                }
            }
            return variants;
        }

        const variants: RequestVariant[] = [];
        for (const format of outputFormats) {
            variants.push(
                ...buildVariantsForBaseUrl(baseUrlHits(selectedLayers.join(','), format)),
                ...buildVariantsForBaseUrl(baseUrlHitsXml(selectedLayers.join(','))),
                ...buildVariantsForBaseUrl(baseUrlFallback(selectedLayers.join(','), format))
            );
        }
        return variants;
    }

    private getPreferredOutputFormats(server: MapOverlayInterface): string[] {
        const requestedFormat = String(server?.outputFormat || '').trim();
        return [requestedFormat || 'geojson'];
    }

    private getPreferredExpectedSrsOrder(server: MapOverlayInterface): ExpectedSrs[] {
        const validSrs: ExpectedSrs[] = ['EPSG:28992', 'CRS:84', 'EPSG:4326', 'EPSG:3857', 'AUTO'];
        const requestedSrs = String(server?.expectedCRS || '').toUpperCase();
        const normalized = requestedSrs === 'EPSG:900913' ? 'EPSG:3857' : requestedSrs;
        const preferred: ExpectedSrs = validSrs.includes(normalized as ExpectedSrs)
            ? normalized as ExpectedSrs
            : 'EPSG:28992';
        // Always append AUTO as silent last-resort fallback (no srsName param).
        return preferred === 'AUTO' ? ['AUTO'] : [preferred, 'AUTO'];
    }

    private buildDefaultCrsVariants(baseUrl: string, expectedSrsOrder: ExpectedSrs[]): RequestVariant[] {
        return expectedSrsOrder.map((expectedSrs) => {
            if (expectedSrs === 'AUTO') {
                return {url: baseUrl, expectedSrs: 'AUTO' as ExpectedSrs};
            }

            return {url: `${baseUrl}&srsName=${expectedSrs}`, expectedSrs: expectedSrs};
        });
    }

    private buildBoundedCrsVariants(baseUrl: string, expectedSrsOrder: ExpectedSrs[], rdBboxParam: string, crs84BboxParam: string): RequestVariant[] {
        const boundedVariants: {[srs in 'EPSG:28992' | 'CRS:84' | 'AUTO']: RequestVariant} = {
            'EPSG:28992': {url: `${baseUrl}&srsName=EPSG:28992${rdBboxParam}`, expectedSrs: 'EPSG:28992'},
            'CRS:84': {url: `${baseUrl}&srsName=CRS:84${crs84BboxParam}`, expectedSrs: 'CRS:84'},
            'AUTO': {url: `${baseUrl}${crs84BboxParam}`, expectedSrs: 'AUTO'},
        };

        const boundedOrder = expectedSrsOrder
            .filter((srs) => srs === 'EPSG:28992' || srs === 'CRS:84' || srs === 'AUTO') as ('EPSG:28992' | 'CRS:84' | 'AUTO')[];

        return boundedOrder.map((srs) => boundedVariants[srs]);
    }

    private getMaxFeaturesForZoom(zoomLevel: number | null): number | null {
        if (zoomLevel === null || zoomLevel === undefined) {
            return MapWFSLayerGenerator.MAX_WFS_FEATURES_REQUEST;
        }

        if (zoomLevel <= 14) {
            return MapWFSLayerGenerator.LOW_WFS_FEATURES_REQUEST;
        }

        if (zoomLevel <= 18) {
            return MapWFSLayerGenerator.MID_WFS_FEATURES_REQUEST;
        }

        if (zoomLevel <= 20) {
            return MapWFSLayerGenerator.MAX_WFS_FEATURES_REQUEST;
        }

        return MapWFSLayerGenerator.MAX_WFS_FEATURES_REQUEST;
    }

    private getCrs84BboxParameter(viewportBounds: WfsViewportBounds): string {
        return `&bbox=${viewportBounds.west},${viewportBounds.south},${viewportBounds.east},${viewportBounds.north},${encodeURIComponent('CRS:84')}`;
    }

    private getRdBboxParameter(viewportBounds: WfsViewportBounds): string {
        const sw = Utils.wgs2rd_2(viewportBounds.south, viewportBounds.west);
        const ne = Utils.wgs2rd_2(viewportBounds.north, viewportBounds.east);

        const minX = Math.min(sw[1], ne[1]);
        const minY = Math.min(sw[0], ne[0]);
        const maxX = Math.max(sw[1], ne[1]);
        const maxY = Math.max(sw[0], ne[0]);

        return `&bbox=${minX},${minY},${maxX},${maxY},${encodeURIComponent('EPSG:28992')}`;
    }

    private parseWFSGeoJson(rawText: any): any {
        try {
            return JSON.parse(rawText as string);
        } catch (strictParseError) {
            const raw = String(rawText);

            // Server returned an XML/HTML error document — not a parse failure, just a bad variant.
            if (raw.trimStart().startsWith('<')) {
                return null;
            }

            try {
                const sanitized = raw.replace(/"geometry":\{"type":"[^"]*",\}/g, '"geometry":null');
                return JSON.parse(sanitized);
            } catch (fallbackParseError) {
                console.error('[WFS] JSON parse error:', strictParseError);
                return null;
            }
        }
    }

    private parseWFSFeatureCount(rawText: any, expectedSrs: ExpectedSrs = 'AUTO'): WfsFeatureCountResult | null {
        if (rawText === null || rawText === undefined) {
            return null;
        }

        const readNonNegativeInt = (value: any): number | null => {
            if (typeof value === 'number' && Number.isFinite(value)) {
                return Math.max(0, Math.floor(value));
            }

            if (typeof value === 'string' && /^[0-9]+$/.test(value.trim())) {
                return Math.max(0, parseInt(value.trim(), 10));
            }

            return null;
        };

        const parseFromObject = (parsed: any): WfsFeatureCountResult | null => {
            let geometryType: string | null = null;
            let samplePosition: {lat: number, lng: number} | null = null;
            if (Array.isArray(parsed?.features) && parsed.features.length > 0) {
                geometryType = parsed.features[0]?.geometry?.type || null;
                samplePosition = this.getFeatureAnchorPosition(parsed, parsed.features[0], expectedSrs) || null;
            }

            const numberMatched = readNonNegativeInt(parsed?.numberMatched);
            if (numberMatched !== null) {
                return {count: numberMatched, geometryType: geometryType, samplePosition: samplePosition};
            }

            const totalFeatures = readNonNegativeInt(parsed?.totalFeatures);
            if (totalFeatures !== null) {
                return {count: totalFeatures, geometryType: geometryType, samplePosition: samplePosition};
            }

            const numberReturned = readNonNegativeInt(parsed?.numberReturned);
            if (numberReturned !== null && numberReturned > 0) {
                return {count: numberReturned, geometryType: geometryType, samplePosition: samplePosition};
            }

            if (Array.isArray(parsed?.features)) {
                return {count: Math.max(0, parsed.features.length), geometryType: geometryType, samplePosition: samplePosition};
            }

            return null;
        };

        if (typeof rawText === 'object') {
            return parseFromObject(rawText);
        }

        const raw = String(rawText);

        try {
            const parsed = JSON.parse(raw);
            const parsedCount = parseFromObject(parsed);
            if (parsedCount !== null) {
                return parsedCount;
            }
        } catch (_ignored) {
            // Continue with XML/regex parser.
        }

        const matched = raw.match(/(?:numberMatched|numberOfFeatures|numberReturned)=['\"]([0-9]+)['\"]/i);
        if (matched && matched[1] !== undefined) {
            return {count: Math.max(0, parseInt(matched[1], 10)), geometryType: null, samplePosition: null};
        }

        return null;
    }

    private normalizeWFSGeoJson(geoJson: any, server: MapOverlayInterface, expectedSrs: ExpectedSrs): any {
        if (!geoJson || !Array.isArray(geoJson.features) || geoJson.features.length <= 0) {
            return geoJson;
        }

        const crsName = this.getWfsCrsName(geoJson);
        const sampleCoordinate = this.getFirstCoordinateFromGeoJson(geoJson);
        const converter = this.getWfsCoordinateConverter(expectedSrs, crsName, sampleCoordinate);

        if (!converter) {
            return geoJson;
        }

        const normalizedFeatures = geoJson.features.map((feature: any) => {
            if (!feature || !feature.geometry) {
                return feature;
            }

            return {
                ...feature,
                geometry: this.mapGeoJsonGeometryCoordinates(feature.geometry, converter),
            };
        });

        this.logger.log('[MapWFSLayerGenerator] Normalized WFS coordinates for server: ' + server.label + ' detected CRS: ' + (crsName || 'heuristic'));

        const normalizedGeoJson = {
            ...geoJson,
            features: normalizedFeatures,
        };

        return normalizedGeoJson;
    }

    private getWfsCrsName(geoJson: any): string {
        const value = geoJson?.crs?.properties?.name || geoJson?.crs?.name || '';
        return String(value).toUpperCase();
    }

    private getWfsCoordinateConverter(expectedSrs: ExpectedSrs, responseCrsName: string, sampleCoordinate: number[] | null): ((coordinate: number[]) => number[]) | null {
        if (!sampleCoordinate || sampleCoordinate.length < 2) {
            return null;
        }

        const x = Number(sampleCoordinate[0]);
        const y = Number(sampleCoordinate[1]);
        const looksGeographic = Math.abs(x) <= 180 && Math.abs(y) <= 90;

        // Primary strategy: trust requested output SRS.
        if (expectedSrs === 'CRS:84') {
            return this.getGeographicSwapConverter(sampleCoordinate);
        }

        if (expectedSrs === 'EPSG:4326') {
            return this.getGeographicSwapConverter(sampleCoordinate);
        }

        if (expectedSrs === 'EPSG:3857') {
            if (looksGeographic) {
                // Server ignored requested SRS and already returned geographic coordinates.
                return this.getGeographicSwapConverter(sampleCoordinate);
            }

            const axisOrder = this.getBestAxisOrderForMercator(sampleCoordinate);
            return (coordinate: number[]) => this.convertMercatorToWgs84Coordinate(coordinate, axisOrder);
        }

        if (expectedSrs === 'EPSG:28992') {
            if (looksGeographic) {
                // Server ignored requested SRS and already returned geographic coordinates.
                return this.getGeographicSwapConverter(sampleCoordinate);
            }

            const axisOrder = this.getBestAxisOrderForRd(sampleCoordinate);
            return (coordinate: number[]) => this.convertRdToWgs84Coordinate(coordinate, axisOrder);
        }

        // Fallback for AUTO requests: rely on advertised response CRS only.
        const normalizedName = (responseCrsName || '').toUpperCase();
        if (normalizedName.includes('28992')) {
            const axisOrder = this.getBestAxisOrderForRd(sampleCoordinate);
            return (coordinate: number[]) => this.convertRdToWgs84Coordinate(coordinate, axisOrder);
        }

        if (normalizedName.includes('3857') || normalizedName.includes('102100') || normalizedName.includes('900913')) {
            const axisOrder = this.getBestAxisOrderForMercator(sampleCoordinate);
            return (coordinate: number[]) => this.convertMercatorToWgs84Coordinate(coordinate, axisOrder);
        }

        if (normalizedName.includes('4326') || normalizedName.includes('CRS:84')) {
            return this.getGeographicSwapConverter(sampleCoordinate);
        }

        // Unknown CRS metadata: only transform if the coordinate range proves it is projected.
        if (!looksGeographic) {
            if (x > 10000 && y > 100000 && x < 400000 && y < 700000) {
                const axisOrder = this.getBestAxisOrderForRd(sampleCoordinate);
                return (coordinate: number[]) => this.convertRdToWgs84Coordinate(coordinate, axisOrder);
            }

            const axisOrder = this.getBestAxisOrderForMercator(sampleCoordinate);
            return (coordinate: number[]) => this.convertMercatorToWgs84Coordinate(coordinate, axisOrder);
        }

        return null;
    }

    private getGeographicSwapConverter(sampleCoordinate: number[] | null): ((coordinate: number[]) => number[]) | null {
        if (!sampleCoordinate || sampleCoordinate.length < 2) {
            return null;
        }

        const x = Number(sampleCoordinate[0]);
        const y = Number(sampleCoordinate[1]);

        // Some broken services return EPSG:4326 as lat,lon in GeoJSON; detect NL-like swapped pairs.
        const looksSwappedNl = (x >= 50 && x <= 55 && y >= 3 && y <= 8);
        if (looksSwappedNl) {
            return (coordinate: number[]) => this.swapCoordinate(coordinate);
        }

        return null;
    }

    private mapGeoJsonGeometryCoordinates(geometry: any, converter: (coordinate: number[]) => number[]): any {
        if (!geometry || !Array.isArray(geometry.coordinates)) {
            return geometry;
        }

        return {
            ...geometry,
            coordinates: this.mapGeoJsonCoordinatesRecursive(geometry.coordinates, converter),
        };
    }

    private mapGeoJsonCoordinatesRecursive(coordinates: any, converter: (coordinate: number[]) => number[]): any {
        if (!Array.isArray(coordinates) || coordinates.length <= 0) {
            return coordinates;
        }

        if (typeof coordinates[0] === 'number') {
            return converter(coordinates as number[]);
        }

        return coordinates.map((entry: any) => this.mapGeoJsonCoordinatesRecursive(entry, converter));
    }

    private getFirstCoordinateFromGeoJson(geoJson: any): number[] | null {
        if (!geoJson || !Array.isArray(geoJson.features)) {
            return null;
        }

        for (let i = 0; i < geoJson.features.length; i++) {
            const coordinate = this.getFirstCoordinateFromGeometry(geoJson.features[i]?.geometry);
            if (coordinate) {
                return coordinate;
            }
        }

        return null;
    }

    private getFirstCoordinateFromGeometry(geometry: any): number[] | null {
        if (!geometry || !Array.isArray(geometry.coordinates)) {
            return null;
        }

        let current = geometry.coordinates;
        while (Array.isArray(current) && current.length > 0) {
            if (typeof current[0] === 'number') {
                return current as number[];
            }
            current = current[0];
        }

        return null;
    }

    private getFeatureAnchorPosition(parsedCollection: any, feature: any, expectedSrs: ExpectedSrs): {lat: number, lng: number} | null {
        const coordinate = this.getFirstCoordinateFromGeometry(feature?.geometry);
        if (!coordinate || coordinate.length < 2) {
            return null;
        }

        const responseCrsName = this.getWfsCrsName(parsedCollection);
        const converter = this.getWfsCoordinateConverter(expectedSrs, responseCrsName, coordinate);
        const normalizedCoordinate = converter ? converter(coordinate) : coordinate;

        const lng = Number(normalizedCoordinate[0]);
        const lat = Number(normalizedCoordinate[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return null;
        }

        if (Math.abs(lng) > 180 || Math.abs(lat) > 90) {
            return null;
        }

        return {lat: lat, lng: lng};
    }

    private convertRdToWgs84Coordinate(coordinate: number[], axisOrder: AxisOrder = 'xy'): number[] {
        if (!Array.isArray(coordinate) || coordinate.length < 2) {
            return coordinate;
        }

        const rdX = Number(axisOrder === 'yx' ? coordinate[1] : coordinate[0]);
        const rdY = Number(axisOrder === 'yx' ? coordinate[0] : coordinate[1]);
        const wgs = Utils.rd2wgs_2(rdX, rdY);

        if (!Array.isArray(wgs) || wgs.length < 2) {
            return coordinate;
        }

        const normalized = [wgs[1], wgs[0]];

        if (coordinate.length > 2) {
            return normalized.concat(coordinate.slice(2));
        }

        return normalized;
    }

    private convertMercatorToWgs84Coordinate(coordinate: number[], axisOrder: AxisOrder = 'xy'): number[] {
        if (!Array.isArray(coordinate) || coordinate.length < 2) {
            return coordinate;
        }

        const x = Number(axisOrder === 'yx' ? coordinate[1] : coordinate[0]);
        const y = Number(axisOrder === 'yx' ? coordinate[0] : coordinate[1]);
        const originShift = 20037508.342789244;

        const lon = (x / originShift) * 180;
        const lat = (Math.atan(Math.exp((y / originShift) * Math.PI)) * 360 / Math.PI) - 90;
        const normalized = [lon, lat];

        if (coordinate.length > 2) {
            return normalized.concat(coordinate.slice(2));
        }

        return normalized;
    }

    private getBestAxisOrderForRd(sampleCoordinate: number[] | null): AxisOrder {
        if (!sampleCoordinate || sampleCoordinate.length < 2) {
            return 'xy';
        }

        const convertedXY = this.convertRdToWgs84Coordinate(sampleCoordinate, 'xy');
        const convertedYX = this.convertRdToWgs84Coordinate(sampleCoordinate, 'yx');
        return this.pickBestAxisOrder(convertedXY, convertedYX);
    }

    private getBestAxisOrderForMercator(sampleCoordinate: number[] | null): AxisOrder {
        if (!sampleCoordinate || sampleCoordinate.length < 2) {
            return 'xy';
        }

        const convertedXY = this.convertMercatorToWgs84Coordinate(sampleCoordinate, 'xy');
        const convertedYX = this.convertMercatorToWgs84Coordinate(sampleCoordinate, 'yx');
        return this.pickBestAxisOrder(convertedXY, convertedYX);
    }

    private pickBestAxisOrder(convertedXY: number[], convertedYX: number[]): AxisOrder {
        const xyInNl = this.isLikelyDutchCoordinate(convertedXY);
        const yxInNl = this.isLikelyDutchCoordinate(convertedYX);

        if (xyInNl && !yxInNl) {
            return 'xy';
        }

        if (!xyInNl && yxInNl) {
            return 'yx';
        }

        return 'xy';
    }

    private isLikelyDutchCoordinate(coordinate: number[]): boolean {
        if (!coordinate || coordinate.length < 2) {
            return false;
        }

        const lon = Number(coordinate[0]);
        const lat = Number(coordinate[1]);
        return lon >= 3 && lon <= 8 && lat >= 50 && lat <= 54.5;
    }

    private swapCoordinate(coordinate: number[]): number[] {
        if (!Array.isArray(coordinate) || coordinate.length < 2) {
            return coordinate;
        }

        const swapped = [coordinate[1], coordinate[0]];
        if (coordinate.length > 2) {
            return swapped.concat(coordinate.slice(2));
        }

        return swapped;
    }

}
